/**
 * Die Zelle als Bild: Steinwand, vergittertes Fenster, Pritsche und die
 * Gittertür mit einem Vorhängeschloss je Spur. Offene Schlösser springen auf,
 * sind alle offen, schwingt die Tür auf.
 */
function Padlock({ x, y, open, label, active, onClick }: { x: number; y: number; open: boolean; label: string; active: boolean; onClick: () => void }) {
  return (
    <g transform={`translate(${x} ${y})`} onClick={onClick} className="cursor-pointer">
      {/* Bügel – offen nach oben gezogen und gekippt */}
      <path
        d="M -9 0 V -10 A 9 9 0 0 1 9 -10 V 0"
        fill="none"
        stroke={open ? '#4ade80' : '#d4d4d8'}
        strokeWidth={4}
        style={{
          transform: open ? 'translate(0px, -8px) rotate(-25deg)' : 'none',
          transformOrigin: '-9px 0px',
          transition: 'transform 400ms ease-out',
        }}
      />
      <rect
        x={-14}
        y={-2}
        width={28}
        height={24}
        rx={4}
        fill={open ? '#15803d' : '#b45309'}
        stroke={active ? '#fde047' : '#18181b'}
        strokeWidth={active ? 3 : 2}
      />
      <text x={0} y={15} textAnchor="middle" fontSize={12} fontWeight={700} fill="#fff">
        {open ? '✓' : label}
      </text>
    </g>
  );
}

export function CellScene({
  total,
  solved,
  active,
  onSelect,
}: {
  total: number;
  solved: number[];
  active: number | null;
  onSelect: (lock: number) => void;
}) {
  const free = solved.length >= total;
  const bars = Array.from({ length: 9 }, (_, i) => 196 + i * 16);

  return (
    <svg viewBox="0 0 360 260" className="w-full select-none rounded-lg" role="img" aria-label={`Zelle, ${solved.length} von ${total} Schlössern offen`}>
      <defs>
        <pattern id="bricks" width="40" height="20" patternUnits="userSpaceOnUse">
          <rect width="40" height="20" fill="#3f3f46" />
          <path d="M0 0 H40 M0 10 H40 M10 0 V10 M30 10 V20" stroke="#27272a" strokeWidth="1.5" />
        </pattern>
        <radialGradient id="lamp" cx="0.5" cy="0" r="0.9">
          <stop offset="0" stopColor="#fde68a" stopOpacity="0.35" />
          <stop offset="1" stopColor="#fde68a" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="freedom" x1="0" x2="1">
          <stop offset="0" stopColor="#fef9c3" />
          <stop offset="1" stopColor="#facc15" />
        </linearGradient>
      </defs>

      {/* Wand und Boden */}
      <rect width="360" height="260" fill="url(#bricks)" />
      <polygon points="0,215 360,215 360,260 0,260" fill="#27272a" />
      <rect width="360" height="260" fill="url(#lamp)" />

      {/* Fenster mit Gittern */}
      <rect x="40" y="30" width="70" height="48" rx="3" fill="#0c1a33" stroke="#18181b" strokeWidth="4" />
      <circle cx="92" cy="46" r="8" fill="#e5e7eb" opacity="0.85" />
      {[54, 68, 82, 96].map((x) => (
        <line key={x} x1={x} y1={30} x2={x} y2={78} stroke="#52525b" strokeWidth="4" />
      ))}

      {/* Pritsche */}
      <rect x="20" y="170" width="120" height="14" rx="3" fill="#57534e" />
      <rect x="24" y="162" width="40" height="10" rx="4" fill="#d6d3d1" />
      <rect x="26" y="184" width="6" height="31" fill="#44403c" />
      <rect x="128" y="184" width="6" height="31" fill="#44403c" />

      {/* Kritzelei an der Wand */}
      <text x="40" y="120" fontSize="10" fill="#a1a1aa" fontFamily="monospace" opacity="0.7">
        |||| |||| ||
      </text>
      <text x="36" y="140" fontSize="9" fill="#a1a1aa" fontFamily="monospace" opacity="0.7">
        ps aux lügt nicht
      </text>

      {/* Türöffnung: dahinter die Freiheit */}
      <rect x="186" y="34" width="150" height="181" fill={free ? 'url(#freedom)' : '#09090b'} style={{ transition: 'fill 600ms' }} />

      {/* Gittertür – schwingt am linken Scharnier auf */}
      <g
        style={{
          transform: free ? 'perspective(600px) rotateY(-75deg)' : 'none',
          transformOrigin: '186px 120px',
          transition: 'transform 900ms ease-in-out',
        }}
      >
        <rect x="186" y="34" width="150" height="181" fill="none" stroke="#71717a" strokeWidth="6" />
        {bars.map((x) => (
          <line key={x} x1={x} y1={34} x2={x} y2={215} stroke="#a1a1aa" strokeWidth="5" />
        ))}
        <rect x="186" y="96" width="150" height="8" fill="#71717a" />
        <rect x="186" y="150" width="150" height="8" fill="#71717a" />
        {Array.from({ length: total }, (_, i) => (
          <Padlock
            key={i}
            x={208 + i * 36}
            y={130}
            label={String(i + 1)}
            open={solved.includes(i)}
            active={active === i}
            onClick={() => onSelect(i)}
          />
        ))}
      </g>

      {/* Rahmen der Tür */}
      <rect x="183" y="31" width="156" height="187" fill="none" stroke="#3f3f46" strokeWidth="4" />

      {free && (
        <text x="261" y="200" textAnchor="middle" fontSize="16" fontWeight="700" fill="#713f12">
          Frei!
        </text>
      )}
    </svg>
  );
}
